import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import FirebaseUserDataService from '../services/firebaseUserData';
import DealAutomationEngine from '../services/DealAutomationEngine';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import toast from 'react-hot-toast';

const SuperiorCRMSystem = ({ contacts = [], deals = [], onAddDeal, onUpdateDeal, onDeleteDeal, onSaveDeals, isDarkMode = false }) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  // Modal States for editing deals
  const [editingDeal, setEditingDeal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  
  // Deal form state
  const [newDeal, setNewDeal] = useState({
    title: '',
    value: '',
    stage: 'prospects',
    contactId: '',
    contactName: '',
    contactEmail: '',
    company: '',
    phone: '',
    closeDate: '',
    notes: ''
  });

  // Convert deals to high-value leads display format
  const convertDealsToLeads = () => {
    return deals.map(deal => ({
      id: deal.id,
      name: deal.title || 'Unnamed Deal',
      company: deal.company || 'No Company',
      email: deal.email || 'No Email',
      phone: deal.phone || 'N/A',
      source: 'Deal Pipeline',
      score: deal.stage === 'closed_won' ? 100 : deal.stage === 'negotiation' ? 95 : deal.stage === 'proposal' ? 90 : 85,
      stage: deal.stage === 'closed_won' ? 'Won' : deal.stage === 'negotiation' ? 'Negotiation' : deal.stage === 'proposal' ? 'Proposal' : 'Prospect',
      value: typeof deal.value === 'number' ? `$${deal.value.toLocaleString()}` : `$${deal.value}`,
      lastContact: deal.lastContact || 'Recently updated',
      nextAction: deal.nextAction || 'Follow up required',
      aiInsights: deal.notes || 'Deal from pipeline',
      socialActivity: `Deal stage: ${deal.stage}`
    }));
  };

  // Use deals as leads - no separate leads system
  const leads = convertDealsToLeads();

  // Calculate live pipeline stats from real data
  const calculatePipelineStats = () => {
    const totalDeals = deals.length;
    const closedDeals = deals.filter(deal => deal.stage === 'closed_won').length;
    const lostDeals = deals.filter(deal => deal.stage === 'closed_lost').length;
    const activeDeals = deals.filter(deal => 
      deal.stage !== 'closed_won' && deal.stage !== 'closed_lost'
    ).length;
    
    // Parse deal values more robustly
    const parseDealValue = (value) => {
      if (!value) return 0;
      const numericValue = typeof value === 'string' ? 
        parseFloat(value.replace(/[$,]/g, '')) : parseFloat(value);
      return isNaN(numericValue) ? 0 : numericValue;
    };
    
    const totalValue = deals.reduce((sum, deal) => sum + parseDealValue(deal.value), 0);
    const closedValue = deals.filter(deal => deal.stage === 'closed_won')
                           .reduce((sum, deal) => sum + parseDealValue(deal.value), 0);
    
    const conversionRate = totalDeals > 0 ? Math.round((closedDeals / totalDeals) * 100) : 0;
    const avgDealSize = totalDeals > 0 ? Math.round(totalValue / totalDeals) : 0;
    
    // Calculate real average sales cycle from deal dates
    const calculateSalesCycle = () => {
      const closedWonDeals = deals.filter(deal => deal.stage === 'closed_won');
      if (closedWonDeals.length === 0) return 24; // Default if no data
      
      let totalDays = 0;
      let validDeals = 0;
      
      closedWonDeals.forEach(deal => {
        if (deal.createdAt && deal.closeDate) {
          const created = new Date(deal.createdAt);
          const closed = new Date(deal.closeDate);
          const daysDiff = Math.round((closed - created) / (1000 * 60 * 60 * 24));
          
          if (daysDiff > 0 && daysDiff < 365) { // Reasonable bounds
            totalDays += daysDiff;
            validDeals++;
          }
        }
      });
      
      return validDeals > 0 ? Math.round(totalDays / validDeals) : 24;
    };
    
    const avgSalesCycle = calculateSalesCycle();
    
    return {
      totalValue: `$${totalValue.toLocaleString()}`,
      closedDeals: `$${closedValue.toLocaleString()}`,
      activeDeals: activeDeals,
      conversionRate: conversionRate,
      avgDealSize: `$${avgDealSize.toLocaleString()}`,
      salesCycle: `${avgSalesCycle} days`,
      lostDeals: lostDeals,
      totalContactsAndDeals: contacts.length + deals.length + leads.length
    };
  };

  const pipelineStats = calculatePipelineStats();

  const [automationRules] = useState([
    {
      name: 'High-Score Lead Alert',
      trigger: 'Lead score > 90',
      action: 'Instant notification + auto-assign to top rep',
      status: 'Ready'
    },
    {
      name: 'Social Engagement Follow-up',
      trigger: 'Social media interaction',
      action: 'Send personalized message within 30 minutes',
      status: 'Ready'
    },
    {
      name: 'Proposal Follow-up',
      trigger: '48 hours after proposal sent',
      action: 'AI-crafted follow-up email + calendar link',
      status: 'Ready'
    },
    {
      name: 'Contract Reminder',
      trigger: 'Contract pending > 5 days',
      action: 'Escalate to manager + priority flag',
      status: 'Ready'
    }
  ]);

  // Deal Management Functions
  const handleAddDeal = () => {
    setNewDeal({
      title: '',
      value: '',
      stage: 'prospects',
      contactId: '',
      contactName: '',
      contactEmail: '',
      company: '',
      phone: '',
      closeDate: '',
      notes: ''
    });
    setEditingDeal(null);
    setShowAddDealModal(true);
  };

  const handleEditDeal = (deal) => {
    setNewDeal({
      title: deal.title || '',
      value: deal.value || '',
      stage: deal.stage || 'prospects',
      contactId: deal.contactId || '',
      contactName: deal.contactName || '',
      contactEmail: deal.contactEmail || '',
      company: deal.company || '',
      phone: deal.phone || '',
      closeDate: deal.closeDate || '',
      notes: deal.notes || ''
    });
    setEditingDeal(deal);
    setShowAddDealModal(true);
  };

  const handleSaveDeal = async (e) => {
    e.preventDefault();
    
    if (!newDeal.title.trim()) return;
    
    // Get contact information if contactId is selected
    const selectedContact = newDeal.contactId ? contacts.find(c => c.id === newDeal.contactId) : null;
    
    const dealData = {
      ...newDeal,
      id: editingDeal ? editingDeal.id : `deal_${Date.now()}`,
      contactName: selectedContact ? selectedContact.name : newDeal.contactName || '',
      contactEmail: selectedContact ? selectedContact.email : newDeal.contactEmail || '',
      company: selectedContact ? (selectedContact.company || '') : newDeal.company || '',
      phone: selectedContact ? (selectedContact.phone || '') : newDeal.phone || '',
      createdAt: editingDeal ? editingDeal.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      movedToStageAt: editingDeal ? editingDeal.movedToStageAt : new Date().toISOString(),
      daysInStage: editingDeal ? editingDeal.daysInStage : 0,
      lastActivity: new Date().toISOString(),
      probability: editingDeal ? editingDeal.probability : 30
    };

    if (editingDeal) {
      // Update existing deal
      const updatedDeals = deals.map(deal => 
        deal.id === editingDeal.id ? dealData : deal
      );
      if (onSaveDeals) await onSaveDeals(updatedDeals);
      toast.success('Deal updated successfully!');
    } else {
      // Add new deal
      const updatedDeals = [...deals, dealData];
      if (onSaveDeals) await onSaveDeals(updatedDeals);
      toast.success(`Deal created: ${dealData.title}`);
    }

    setShowAddDealModal(false);
    setEditingDeal(null);
    setNewDeal({
      title: '',
      value: '',
      stage: 'prospects',
      contactId: '',
      contactName: '',
      contactEmail: '',
      company: '',
      phone: '',
      closeDate: '',
      notes: ''
    });
  };

  const handleDeleteDeal = async (dealId) => {
    if (confirm('Are you sure you want to delete this deal?')) {
      const updatedDeals = deals.filter(deal => deal.id !== dealId);
      if (onSaveDeals) await onSaveDeals(updatedDeals);
    }
  };

  const handleUpdateDealStage = async (dealId, newStage) => {
    const updatedDeals = deals.map(deal =>
      deal.id === dealId ? { ...deal, stage: newStage, updatedAt: new Date().toISOString() } : deal
    );
    if (onSaveDeals) await onSaveDeals(updatedDeals);
  };

  const exportData = () => {
    const csvContent = leads.map(lead => 
      `${lead.name},${lead.company},${lead.email},${lead.phone},${lead.source},${lead.score},${lead.stage},${lead.value}`
    ).join('\n');
    
    const blob = new Blob([`Name,Company,Email,Phone,Source,Score,Stage,Value\n${csvContent}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crm_leads.csv';
    a.click();
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-red-600 bg-red-100';
    if (score >= 80) return 'text-orange-600 bg-orange-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getStageColor = (stage) => {
    switch(stage) {
      case 'Hot Lead': return 'bg-red-500';
      case 'Qualified': return 'bg-orange-500';
      case 'Negotiation': return 'bg-blue-500';
      case 'Nurturing': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  // Handle drag-and-drop deal movement
  const handleDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }
    
    const { source, destination, draggableId } = result;
    
    // No change if dropped in same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }
    
    const dealId = draggableId;
    const oldStage = source.droppableId;
    const newStage = destination.droppableId;
    
    // Find the deal being moved - try exact match first, then string match
    let dealToMove = deals.find(d => d.id === dealId);
    if (!dealToMove) {
      dealToMove = deals.find(d => String(d.id) === String(dealId));
    }
    
    if (!dealToMove) {
      console.error('Deal not found:', dealId);
      toast.error('Deal not found. Please refresh the page.');
      return;
    }
    
    try {
      // Optimistic update
      const updatedDeals = deals.map(deal => {
        if (deal.id === dealId || String(deal.id) === String(dealId)) {
          return {
            ...deal,
            stage: newStage,
            movedToStageAt: new Date().toISOString(),
            daysInStage: 0,
            updatedAt: new Date().toISOString()
          };
        }
        return deal;
      });
      
      // Update UI immediately
      if (onSaveDeals) {
        await onSaveDeals(updatedDeals);
      }
      
      // Show feedback
      const stageDisplay = newStage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      toast.success(`Deal moved to ${stageDisplay}`, {
        icon: newStage === 'closed_won' ? '🎉' : '✅',
        duration: 3000
      });
      
      // Trigger stage-based automation
      if (tenant?.id) {
        await DealAutomationEngine.handleStageChange(
          { ...dealToMove, stage: newStage },
          oldStage,
          newStage,
          tenant.id
        );
      }
      
    } catch (error) {
      console.error('Error moving deal:', error);
      toast.error('Failed to move deal. Please try again.');
    }
  };

  return (
    <div className={`${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-indigo-100'} rounded-xl shadow-lg p-6`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-teal-400' : 'text-indigo-800'} flex items-center gap-3`}>
            <span className="text-3xl">👥</span>
            Deal Pipeline Manager
          </h3>
          <p className="text-gray-600 mt-1">Track and manage your sales deals through the pipeline stages</p>
        </div>
        <div className="flex items-center gap-6">
          <button 
            onClick={handleAddDeal}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg flex items-center gap-2 font-medium"
          >
            <span className="text-lg">💼</span>
            Create Deal
          </button>
          <div className="text-right">
            <div className="text-sm text-gray-500">Pipeline Value</div>
            <div className="text-2xl font-bold text-indigo-600">{pipelineStats.totalValue}</div>
            <div className="text-xs text-gray-500">{pipelineStats.conversionRate}% conversion rate</div>
          </div>
        </div>
      </div>



      {/* CRM Stats Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-600 border border-gray-500' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'} rounded-lg p-3`}>
          <div className="text-xs text-green-600 font-medium">CLOSED DEALS</div>
          <div className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-800'}`}>{pipelineStats.closedDeals}</div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-600 border border-gray-500' : 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200'} rounded-lg p-3`}>
          <div className="text-xs text-blue-600 font-medium">ACTIVE DEALS</div>
          <div className={`text-lg font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>{pipelineStats.activeDeals}</div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-600 border border-gray-500' : 'bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200'} rounded-lg p-3`}>
          <div className="text-xs text-purple-600 font-medium">CONVERSION</div>
          <div className={`text-lg font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-800'}`}>{pipelineStats.conversionRate}%</div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-600 border border-gray-500' : 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200'} rounded-lg p-3`}>
          <div className="text-xs text-orange-600 font-medium">AVG DEAL</div>
          <div className={`text-lg font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-800'}`}>{pipelineStats.avgDealSize}</div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-600 border border-gray-500' : 'bg-gradient-to-r from-red-50 to-pink-50 border border-red-200'} rounded-lg p-3`}>
          <div className="text-xs text-red-600 font-medium">SALES CYCLE</div>
          <div className={`text-lg font-bold ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>{pipelineStats.salesCycle}</div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-600 border border-gray-500' : 'bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200'} rounded-lg p-3`}>
          <div className="text-xs text-indigo-600 font-medium">AI INSIGHTS</div>
          <div className={`text-lg font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-800'}`}>{pipelineStats.totalContactsAndDeals}</div>
        </div>
      </div>

      {/* Pipeline Deals Overview - Kanban Board */}
      <div className="mb-8">
        <h4 className={`text-xl font-semibold ${isDarkMode ? 'text-teal-400' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
          Pipeline Deals Overview
          <span className="text-sm font-normal text-gray-500">(Drag deals between stages)</span>
        </h4>
        
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Prospects Column */}
            <PipelineStage
              stageId="prospects"
              stageName="Prospects"
              stageIcon="🎯"
              stageColor="bg-purple-50 border-purple-200"
              deals={deals.filter(d => d.stage === 'prospects')}
              isDarkMode={isDarkMode}
              onEditDeal={handleEditDeal}
              onDeleteDeal={handleDeleteDeal}
            />
            
            {/* Qualified Column */}
            <PipelineStage
              stageId="qualified"
              stageName="Qualified"
              stageIcon="✅"
              stageColor="bg-blue-50 border-blue-200"
              deals={deals.filter(d => d.stage === 'qualified')}
              isDarkMode={isDarkMode}
              onEditDeal={handleEditDeal}
              onDeleteDeal={handleDeleteDeal}
            />
            
            {/* Proposal Column */}
            <PipelineStage
              stageId="proposal"
              stageName="Proposal"
              stageIcon="📄"
              stageColor="bg-orange-50 border-orange-200"
              deals={deals.filter(d => d.stage === 'proposal')}
              isDarkMode={isDarkMode}
              onEditDeal={handleEditDeal}
              onDeleteDeal={handleDeleteDeal}
            />
            
            {/* Negotiation Column */}
            <PipelineStage
              stageId="negotiation"
              stageName="Negotiation"
              stageIcon="🤝"
              stageColor="bg-yellow-50 border-yellow-200"
              deals={deals.filter(d => d.stage === 'negotiation')}
              isDarkMode={isDarkMode}
              onEditDeal={handleEditDeal}
              onDeleteDeal={handleDeleteDeal}
            />
            
            {/* Closed Won Column */}
            <PipelineStage
              stageId="closed_won"
              stageName="Closed Won"
              stageIcon="🎉"
              stageColor="bg-green-50 border-green-200"
              deals={deals.filter(d => d.stage === 'closed_won')}
              isDarkMode={isDarkMode}
              onEditDeal={handleEditDeal}
              onDeleteDeal={handleDeleteDeal}
            />
            
            {/* Closed Lost Column */}
            <PipelineStage
              stageId="closed_lost"
              stageName="Closed Lost"
              stageIcon="😞"
              stageColor="bg-gray-50 border-gray-300"
              deals={deals.filter(d => d.stage === 'closed_lost')}
              isDarkMode={isDarkMode}
              onEditDeal={handleEditDeal}
              onDeleteDeal={handleDeleteDeal}
            />
          </div>
        </DragDropContext>
      </div>

      {/* Quick Actions */}
      <div className={`flex justify-between items-center pt-4 ${isDarkMode ? 'border-t border-gray-600' : 'border-t border-gray-200'}`}>
        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {leads.length} high-value leads • {pipelineStats.totalValue} pipeline • {contacts.length} total contacts
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleAddDeal}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            💼 Add New Deal
          </button>
          <button 
            onClick={() => {
              toast.custom((t) => (
                <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-lg w-full ${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white'} shadow-lg rounded-lg pointer-events-auto flex ring-1 ${isDarkMode ? 'ring-gray-600' : 'ring-black'} ring-opacity-5`}>
                  <div className="flex-1 w-0 p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0"><span className="text-2xl">📊</span></div>
                      <div className="ml-3 flex-1">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-teal-400' : 'text-gray-900'}`}>Pipeline Insights</p>
                        <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          <strong>Current Status:</strong><br/>
                          • {pipelineStats.totalDeals} deals in pipeline<br/>
                          • {pipelineStats.activeDeals} active deals<br/>
                          • {pipelineStats.conversionRate}% conversion rate<br/>
                          • ${pipelineStats.avgDealSize} average deal size<br/><br/>
                          <strong>Focus Areas:</strong><br/>
                          → Review deals in proposal and negotiation stages<br/>
                          → Follow up on deals approaching close dates<br/>
                          → Update deal stages to maintain accurate pipeline<br/>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`flex border-l ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <button onClick={() => toast.dismiss(t.id)} className={`w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-500'} focus:outline-none`}>✕</button>
                  </div>
                </div>
              ), { duration: Infinity, position: 'top-right' });
            }}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
          >
            📊 Pipeline Insights
          </button>
          <button 
            onClick={exportData}
            className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors"
          >
            Export Data
          </button>
        </div>
      </div>



      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white'} rounded-xl p-6 w-full max-w-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-teal-400' : 'text-gray-900'}`}>Delete Lead</h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
              Are you sure you want to delete this lead? This action cannot be undone.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => handleDeleteLead(showDeleteConfirm)}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Deal Modal with Optional Contact Selector + Manual Entry */}
      {showAddDealModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white'} rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
            <div className={`sticky top-0 ${isDarkMode ? 'bg-gray-700 border-b border-gray-600' : 'bg-white border-b border-gray-200'} p-6 rounded-t-xl`}>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-teal-400' : 'text-indigo-800'} flex items-center gap-2`}>
                <span className="text-2xl">💼</span>
                {editingDeal ? 'Edit Deal' : 'Create New Deal'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">Select a contact from your list OR enter contact details manually</p>
            </div>
            <form onSubmit={handleSaveDeal} className="p-6 space-y-4">
              
              {/* Contact Selector - OPTIONAL */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <span>👤</span>
                  Quick Select from Contacts (Optional)
                </label>
                <select
                  value={newDeal.contactId}
                  onChange={(e) => {
                    const selectedContactId = e.target.value;
                    if (selectedContactId) {
                      const selectedContact = contacts.find(c => c.id === selectedContactId);
                      if (selectedContact) {
                        setNewDeal({ 
                          ...newDeal, 
                          contactId: selectedContactId,
                          contactName: selectedContact.name || '',
                          contactEmail: selectedContact.email || '',
                          company: selectedContact.company || '',
                          phone: selectedContact.phone || '',
                          title: newDeal.title || `${selectedContact.company || selectedContact.name} - Deal`
                        });
                      }
                    } else {
                      // Clear selection but keep manually entered data
                      setNewDeal({ ...newDeal, contactId: '' });
                    }
                  }}
                  className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">-- Or choose from {contacts.length} contacts to auto-fill --</option>
                  {contacts.map(contact => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name} • {contact.company || 'No Company'} • {contact.email}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-gray-600 mt-2">
                  💡 Tip: Select a contact to auto-fill all fields, or leave blank and enter manually below
                </div>
              </div>

              {/* Manual Contact Entry Fields */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  {/* Contact Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
                    <input
                      type="text"
                      value={newDeal.contactName}
                      onChange={(e) => setNewDeal({ ...newDeal, contactName: e.target.value })}
                      placeholder="John Smith"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  {/* Contact Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
                    <input
                      type="email"
                      value={newDeal.contactEmail}
                      onChange={(e) => setNewDeal({ ...newDeal, contactEmail: e.target.value })}
                      placeholder="john@company.com"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                    <input
                      type="text"
                      value={newDeal.company}
                      onChange={(e) => setNewDeal({ ...newDeal, company: e.target.value })}
                      placeholder="Acme Corp"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={newDeal.phone}
                      onChange={(e) => setNewDeal({ ...newDeal, phone: e.target.value })}
                      placeholder="555-123-4567"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Deal Title */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal Title *</label>
                  <input
                    type="text"
                    value={newDeal.title}
                    onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
                    placeholder="Acme Corp - Enterprise Package"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                {/* Deal Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal Value *</label>
                  <input
                    type="number"
                    value={newDeal.value}
                    onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })}
                    placeholder="50000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  <div className="text-xs text-gray-500 mt-1">Enter amount in dollars (no $)</div>
                </div>

                {/* Pipeline Stage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Stage *</label>
                  <select
                    value={newDeal.stage}
                    onChange={(e) => setNewDeal({ ...newDeal, stage: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="prospects">🎯 Prospects</option>
                    <option value="qualified">✅ Qualified</option>
                    <option value="proposal">📄 Proposal</option>
                    <option value="negotiation">🤝 Negotiation</option>
                    <option value="closed_won">🎉 Closed Won</option>
                    <option value="closed_lost">😞 Closed Lost</option>
                  </select>
                </div>

                {/* Expected Close Date */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close Date</label>
                  <input
                    type="date"
                    value={newDeal.closeDate}
                    onChange={(e) => setNewDeal({ ...newDeal, closeDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Deal Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deal Notes & Requirements</label>
                <textarea
                  value={newDeal.notes}
                  onChange={(e) => setNewDeal({ ...newDeal, notes: e.target.value })}
                  placeholder="Deal notes, customer requirements, next steps, special pricing...\n\nExample:\n- Needs 10 user licenses\n- Interested in annual contract\n- Follow up after demo on Friday"
                  rows="4"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium shadow-lg"
                >
                  {editingDeal ? '✅ Update Deal' : '💼 Create Deal'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDealModal(false);
                    setEditingDeal(null);
                    setNewDeal({
                      title: '',
                      value: '',
                      stage: 'prospects',
                      contactId: '',
                      contactName: '',
                      contactEmail: '',
                      company: '',
                      phone: '',
                      closeDate: '',
                      notes: ''
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

// Pipeline Stage Column Component
const PipelineStage = ({ stageId, stageName, stageIcon, stageColor, deals, isDarkMode, onEditDeal, onDeleteDeal }) => {
  const totalValue = deals.reduce((sum, deal) => {
    const value = typeof deal.value === 'string' ? parseFloat(deal.value.replace(/[$,]/g, '')) : deal.value;
    return sum + (isNaN(value) ? 0 : value);
  }, 0);
  
  return (
    <div className={`${isDarkMode ? 'bg-gray-600 border border-gray-500' : stageColor} rounded-lg p-3`}>
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <h5 className={`font-semibold text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} flex items-center gap-2`}>
            <span>{stageIcon}</span>
            {stageName}
          </h5>
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {deals.length}
          </span>
        </div>
        <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          ${totalValue.toLocaleString()}
        </div>
      </div>
      
      <Droppable droppableId={stageId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`space-y-2 min-h-[200px] ${snapshot.isDraggingOver ? 'bg-opacity-20 bg-blue-500 rounded-lg' : ''}`}
          >
            {deals.map((deal, index) => (
              <DealCard
                key={deal.id}
                deal={deal}
                index={index}
                isDarkMode={isDarkMode}
                onEditDeal={onEditDeal}
                onDeleteDeal={onDeleteDeal}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// Deal Card Component - Enhanced with Contact Info
const DealCard = ({ deal, index, isDarkMode, onEditDeal, onDeleteDeal }) => {
  const dealValue = typeof deal.value === 'string' ? parseFloat(deal.value.replace(/[$,]/g, '')) : deal.value;
  const displayValue = isNaN(dealValue) ? '$0' : `$${dealValue.toLocaleString()}`;
  
  // Calculate probability based on stage
  const getProbability = (stage) => {
    switch(stage) {
      case 'prospects': return 30;
      case 'qualified': return 50;
      case 'proposal': return 65;
      case 'negotiation': return 75;
      case 'closed_won': return 100;
      case 'closed_lost': return 0;
      default: return 30;
    }
  };
  
  const probability = deal.probability || getProbability(deal.stage);
  
  // Calculate days in stage
  const getDaysInStage = () => {
    if (deal.movedToStageAt) {
      const movedDate = new Date(deal.movedToStageAt);
      const now = new Date();
      const diffTime = Math.abs(now - movedDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return deal.daysInStage || 0;
  };
  
  const daysInStage = getDaysInStage();
  const isStuck = daysInStage >= 7 && deal.stage !== 'closed_won' && deal.stage !== 'closed_lost';
  
  return (
    <Draggable draggableId={String(deal.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'} rounded-lg p-3 transition-all hover:shadow-md ${snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400 rotate-2' : ''}`}
        >
          {/* Drag Handle - Top Bar */}
          <div 
            {...provided.dragHandleProps}
            className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 cursor-move hover:bg-gray-50 -mx-3 -mt-3 px-3 pt-3 rounded-t-lg"
            title="Drag to move deal"
          >
            <span className="text-xs text-gray-500 font-medium">☰ Drag to move</span>
            {isStuck && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                ⚠️ {daysInStage} days stuck
              </span>
            )}
          </div>
          
          {/* Deal Title */}
          <h6 className={`font-semibold text-sm mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            {deal.title || 'Untitled Deal'}
          </h6>
          
          {/* CONTACT INFORMATION - PROMINENT */}
          <div className={`mb-3 pb-2 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            {deal.contactName && (
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm">👤</span>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {deal.contactName}
                </span>
              </div>
            )}
            {deal.company && (
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-6`}>
                🏢 {deal.company}
              </div>
            )}
            {deal.contactEmail && (
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-6 truncate`}>
                ✉️ {deal.contactEmail}
              </div>
            )}
            {deal.phone && (
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-6`}>
                📞 {deal.phone}
              </div>
            )}
          </div>
          
          {/* Deal Value */}
          <div className="font-bold text-green-600 mb-2 text-base">
            {displayValue}
          </div>
          
          {/* Probability Bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Probability</span>
              <span className={`font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{probability}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${probability >= 70 ? 'bg-green-500' : probability >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${probability}%` }}
              ></div>
            </div>
          </div>
          
          {/* Days in Stage */}
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
            📅 {daysInStage} {daysInStage === 1 ? 'day' : 'days'} in stage
          </div>
          
          {/* Last Activity */}
          {deal.lastActivity && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
              🕒 Last: {new Date(deal.lastActivity).toLocaleDateString()}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-1 mt-3 pt-2 border-t border-gray-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditDeal(deal);
              }}
              className="flex-1 text-xs bg-blue-100 text-blue-700 px-2 py-1.5 rounded hover:bg-blue-200 transition-colors font-medium"
            >
              ✏️ Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete "${deal.title}"?`)) {
                  onDeleteDeal(deal.id);
                }
              }}
              className="text-xs bg-red-100 text-red-700 px-2 py-1.5 rounded hover:bg-red-200 transition-colors"
            >
              🗑️
            </button>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default SuperiorCRMSystem;
